/**
 * Componente SubscriptionStatusBanner
 * 
 * DESCRIÇÃO:
 * Banner persistente que aparece no topo da aplicação para alertar
 * sobre problemas de assinatura, vencimentos próximos, período de
 * graça e outras situações que requerem ação do usuário.
 * 
 * FUNCIONALIDADES:
 * - Avisos dinâmicos baseados no status da assinatura
 * - Diferentes níveis de urgência com cores específicas
 * - Botões de ação contextual (renovar, gerenciar, etc.)
 * - Cálculo automático de tempo restante
 * - Possibilidade de dispensar temporariamente
 * 
 * CONECTA COM:
 * - useSubscriptionStatus
 * - useFeatureBlocking
 * - Sistema de billing/checkout
 */

import React, { useState } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  AlertTriangle, 
  Clock, 
  CreditCard, 
  CheckCircle, 
  X, 
  ExternalLink,
  Zap
} from 'lucide-react';
import { useSubscriptionStatus, SubscriptionStatus } from '@/hooks/useSubscriptionStatus';
import { useFeatureBlocking } from '@/hooks/useFeatureBlocking';
import { useSubscription } from '@/hooks/useSubscription';

interface SubscriptionStatusBannerProps {
  className?: string;
}

export const SubscriptionStatusBanner = ({ className = '' }: SubscriptionStatusBannerProps) => {
  const { status } = useSubscriptionStatus();
  const { blockingLevel, canUpgrade } = useFeatureBlocking();
  const { openCustomerPortal } = useSubscription();
  const [isDismissed, setIsDismissed] = useState(false);

  // Não mostrar se não há status ou foi dispensado
  if (!status || !status.showWarning || isDismissed) {
    return null;
  }

  const bannerConfig = getBannerConfig(status, blockingLevel);
  
  if (!bannerConfig) {
    return null;
  }

  const handleAction = async () => {
    if (bannerConfig.actionType === 'portal') {
      await openCustomerPortal();
    } else if (bannerConfig.actionType === 'billing') {
      // Redirecionar para página de billing
      window.location.href = '/billing';
    }
  };

  const handleDismiss = () => {
    setIsDismissed(true);
    // Salvar no localStorage para não mostrar novamente nesta sessão
    localStorage.setItem(`dismissed-banner-${status.planName}-${Date.now()}`, 'true');
  };

  return (
    <Alert className={`${bannerConfig.className} ${className} border-l-4 relative`}>
      <bannerConfig.icon className="h-5 w-5" />
      <AlertDescription className="flex items-center justify-between w-full">
        <div className="flex items-center gap-3 flex-1">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-semibold">{bannerConfig.title}</span>
              <Badge variant={bannerConfig.badgeVariant} className="text-xs">
                {status.urgencyLevel.toUpperCase()}
              </Badge>
              {status.planDisplayName && (
                <Badge variant="outline" className="text-xs">
                  {status.planDisplayName}
                </Badge>
              )}
            </div>
            <div className="text-sm">
              {bannerConfig.message}
              {status.isInGracePeriod && status.daysUntilBlocked !== undefined && (
                <div className="text-xs mt-1 opacity-90">
                  {status.daysUntilBlocked} dias restantes antes do bloqueio total
                </div>
              )}
            </div>
          </div>
          
          {/* Informações de tempo restante */}
          {(status.daysUntilExpiration !== undefined || status.daysUntilBlocked !== undefined) && (
            <div className="text-center px-3">
              <div className="text-lg font-bold">
                {status.daysUntilBlocked !== undefined ? status.daysUntilBlocked : status.daysUntilExpiration}
              </div>
              <div className="text-xs opacity-75">
                {status.daysUntilBlocked !== undefined ? 'dias para bloqueio' : 'dias restantes'}
              </div>
            </div>
          )}
        </div>

        {/* Botões de ação */}
        <div className="flex items-center gap-2 ml-4">
          {bannerConfig.showAction && (
            <Button
              onClick={handleAction}
              size="sm"
              variant={bannerConfig.actionVariant}
              className="whitespace-nowrap"
            >
              {bannerConfig.actionIcon && <bannerConfig.actionIcon className="w-4 h-4 mr-1" />}
              {bannerConfig.actionText}
            </Button>
          )}
          
          {/* Botão de dispensar (apenas para avisos menos críticos) */}
          {status.urgencyLevel !== 'critical' && (
            <Button
              onClick={handleDismiss}
              size="sm"
              variant="ghost"
              className="p-1 h-auto"
            >
              <X className="w-4 h-4" />
            </Button>
          )}
        </div>
      </AlertDescription>
    </Alert>
  );
};

// Função para obter configuração do banner baseado no status
const getBannerConfig = (status: SubscriptionStatus, blockingLevel: string) => {
  const baseConfig = {
    showAction: true,
    actionIcon: ExternalLink,
    actionVariant: 'default' as const,
    badgeVariant: 'secondary' as const
  };

  // Trials legados não concedem mais acesso e são tratados como pagamento pendente.
  if (status.planName === 'trial') {
    return {
      ...baseConfig,
      icon: CreditCard,
      title: 'Pagamento pendente',
      message: 'Escolha um plano e conclua o pagamento para liberar o CRM.',
      className: 'border-l-amber-500 bg-amber-50 text-amber-900',
      actionText: 'Ver Planos',
      actionType: 'billing',
      actionVariant: 'default' as const,
      badgeVariant: 'secondary' as const,
      actionIcon: CreditCard
    };
  }

  switch (status.urgencyLevel) {
    case 'critical':
      if (status.isBlocked) {
        return {
          ...baseConfig,
          icon: AlertTriangle,
          title: 'Assinatura Bloqueada',
          message: 'Sua assinatura foi cancelada ou expirou. Acesso limitado até a reativação.',
          className: 'border-l-red-500 bg-red-50 text-red-800',
          actionText: 'Reativar Agora',
          actionType: 'billing',
          actionVariant: 'destructive' as const,
          badgeVariant: 'destructive' as const,
          actionIcon: CreditCard
        };
      }
      return {
        ...baseConfig,
        icon: AlertTriangle,
        title: 'Ação Urgente Necessária',
        message: 'Múltiplas tentativas de pagamento falharam. Acesso será bloqueado em breve.',
        className: 'border-l-red-500 bg-red-50 text-red-800',
        actionText: 'Resolver Pagamento',
        actionType: 'portal',
        actionVariant: 'destructive' as const,
        badgeVariant: 'destructive' as const
      };

    case 'high':
      if (status.isInGracePeriod) {
        return {
          ...baseConfig,
          icon: Clock,
          title: 'Período de Graça Ativo',
          message: 'Sua assinatura venceu mas ainda há tempo para renovar sem perder acesso.',
          className: 'border-l-orange-500 bg-orange-50 text-orange-800',
          actionText: 'Renovar Agora',
          actionType: 'portal',
          actionVariant: 'default' as const,
          badgeVariant: 'secondary' as const,
          actionIcon: Zap
        };
      }
      return {
        ...baseConfig,
        icon: CreditCard,
        title: 'Problema de Pagamento',
        message: 'Detectamos problemas com seu método de pagamento. Resolva para evitar interrupção.',
        className: 'border-l-orange-500 bg-orange-50 text-orange-800',
        actionText: 'Gerenciar Pagamento',
        actionType: 'portal',
        actionVariant: 'default' as const
      };

    case 'medium':
      return {
        ...baseConfig,
        icon: Clock,
        title: 'Renovação Próxima',
        message: 'Sua assinatura vence em breve. Renove agora para evitar interrupções.',
        className: 'border-l-yellow-500 bg-yellow-50 text-yellow-800',
        actionText: 'Renovar',
        actionType: 'portal',
        actionVariant: 'outline' as const,
        badgeVariant: 'outline' as const
      };

    case 'low':
      return {
        ...baseConfig,
        icon: CheckCircle,
        title: 'Renovação Disponível',
        message: 'Sua assinatura está se aproximando do vencimento.',
        className: 'border-l-blue-500 bg-blue-50 text-blue-800',
        actionText: 'Renovar',
        actionType: 'portal',
        actionVariant: 'outline' as const,
        badgeVariant: 'outline' as const
      };

    default:
      return null;
  }
};
